import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { SERVICES } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  useSEO(service ? service.title : "Service", service ? service.intro : "");
  if (!service) return <Navigate to="/services" replace />;
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div>
      <section className="relative bg-[#0B192C] text-white pt-20 pb-24 overflow-hidden" data-testid="service-detail-hero">
        <img src={service.image} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-25 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B192C] via-[#0B192C]/80 to-transparent" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 font-head uppercase tracking-wide text-sm" data-testid="back-to-services"><ArrowLeft size={16} /> All Services</Link>
          <div className="flex items-center gap-3 mb-5"><service.icon className="text-[#FF5A00]" size={28} /><span className="font-head uppercase tracking-[0.3em] text-[#FF5A00] text-sm font-700">Freight Service</span></div>
          <h1 className="font-head font-black uppercase text-6xl md:text-8xl tracking-tighter leading-[0.85] max-w-4xl">{service.title}</h1>
        </div>
      </section>

      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-2xl md:text-3xl font-head font-700 leading-tight text-[#0B192C]">{service.intro}</p>
              <div className="mt-10 space-y-4">
                {service.points.map((p, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white border-2 border-[#0B192C]/10 p-5" data-testid={`service-point-${i}`}>
                    <div className="w-7 h-7 bg-[#FF5A00] flex items-center justify-center flex-shrink-0 mt-0.5"><Check size={16} className="text-white" /></div>
                    <span className="text-[#334155] text-lg">{p}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.1}>
              <div className="bg-[#0B192C] text-white p-8 sticky top-28">
                <h3 className="font-head font-800 uppercase text-2xl">Move this freight</h3>
                <p className="text-[#94A3B8] mt-3">Get an honest, all-in rate for your {service.title.toLowerCase()} shipment.</p>
                <Link to="/quote" data-testid="service-quote-btn" className="mt-6 flex items-center justify-center gap-2 bg-[#FF5A00] text-white font-head font-700 uppercase tracking-[0.15em] px-6 py-4 hover:bg-[#E65100] transition-colors">Get a Quote <ArrowRight size={18} /></Link>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-20">
          <h3 className="font-head font-black uppercase text-3xl tracking-tighter mb-8">Other services</h3>
          <div className="grid md:grid-cols-4 gap-4">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white border-2 border-[#0B192C]/10 p-6 hover:border-[#FF5A00] transition-colors" data-testid={`related-${s.slug}`}>
                <s.icon className="text-[#FF5A00] mb-3" size={22} />
                <div className="font-head font-700 uppercase tracking-tight">{s.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
    </div>
  );
}
